
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { TableColumn } from "@shared/types/table";
import { useEffect, useMemo, useState } from "react";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useAllMeters } from "@features/mx/device-management/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MeterFilters, MeterFiltersType } from "./MeterFilters";
import { GenericTable } from "@shared/components/GenericTable";

interface MeterSelectionStepProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  currentStepIndex?: number;
  totalSteps?: number;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  stepHelpers?: StepHelpers;
  remoteUtilityId: string;
}

export const MeterSelectionStep = ({
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep = false,
  isLastStep = false,
  currentStepIndex = 1,
  totalSteps = 3,
  setValidationError,
  clearValidationError,
  stepHelpers,
  remoteUtilityId,
}: MeterSelectionStepProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
const navigate=useNavigate();
  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");

  // Get all filter params from URL
const getFiltersFromUrl = (): MeterFiltersType => {
  return {
    meterType: searchParams.get("meterType") || undefined, // ✅ camelCase
    service: searchParams.get("service") || undefined,
    manufacturer: searchParams.get("manufacturer") || undefined,
    model: searchParams.get("model") || undefined,
    area: searchParams.get("area") || undefined,
    subarea: searchParams.get("subarea") || undefined,
    premise: searchParams.get("premise") || undefined,
    readingStatus: searchParams.get("readingStatus") || undefined, // ✅ camelCase
    dateFrom: searchParams.get("dateFrom") || undefined, // ✅ camelCase
    dateTo: searchParams.get("dateTo") || undefined, // ✅ camelCase
  };
};

  const [currentFilters, setCurrentFilters] = useState<MeterFiltersType>(
    getFiltersFromUrl()
  );

  // State for selected meters
  const [selectedMeters, setSelectedMeters] = useState<string[]>(
    data.selectedMeterIds || []
  );

  // ✅ FIX 1: Use state for operation type to trigger re-renders
  const [operationType, setOperationType] = useState<number | null>(null);

  // ✅ FIX 2: Track if this is the first load to prevent clearing on mount
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // ✅ FIX 3: Update operation type from step helpers with useEffect
  useEffect(() => {
    if (stepHelpers) {
      const opType = stepHelpers.getDataFromStep(0, "operationType");
      console.log('Operation Type from Step 0:', opType);
      
      // Convert to number if it's a string
      const numericOpType = opType ? Number(opType) : null;
      
      if (numericOpType !== operationType) {
        console.log('Setting operation type:', numericOpType);
        setOperationType(numericOpType);
        
        // ✅ Only clear filters if this is NOT the first load
        if (!isFirstLoad) {
          console.log('Operation type changed, clearing filters and selections');
          // Clear filters in state (don't modify URL here)
          setCurrentFilters({});
          // Clear selected meters
          setSelectedMeters([]);
        }
      }
      
      // Mark first load as complete
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }
    }
  }, [stepHelpers, data]);

  // Sync filters when URL changes
  useEffect(() => {
    setCurrentFilters(getFiltersFromUrl());
  }, [searchParams]);

  // Query params for API
const queryParams = useMemo(() => {
  const params: any = {
    remoteUtilityId,
    page: currentPage,
    limit: PAGE_SIZE,
  };

  // Add search if present
  if (currentSearch) {
    params.search = currentSearch;
  }

  // Add operation type
  if (operationType) {
    params.operationType = operationType;
  }

  // ✅ Convert filters from camelCase to snake_case for API
  if (currentFilters.meterType) {
    params.meter_type = currentFilters.meterType;
  }
  if (currentFilters.service) {
    params.service = currentFilters.service;
  }
  if (currentFilters.manufacturer) {
    params.manufacturer = currentFilters.manufacturer;
  }
  if (currentFilters.model) {
    params.model = currentFilters.model;
  }
  if (currentFilters.area) {
    params.area = currentFilters.area;
  }
  if (currentFilters.subarea) {
    params.subarea = currentFilters.subarea;
  }
  if (currentFilters.premise) {
    params.premise = currentFilters.premise;
  }
  if (currentFilters.readingStatus) {
    params.reading_status = currentFilters.readingStatus;
  }
  if (currentFilters.dateFrom) {
    params.date_from = currentFilters.dateFrom;
  }
  if (currentFilters.dateTo) {
    params.date_to = currentFilters.dateTo;
  }

  console.log('API Query Params (snake_case):', params);
  return params;
}, [remoteUtilityId, currentPage, currentSearch, operationType, currentFilters]);

  // Fetch meters from API
  // const { data: meterList, isLoading } = useMeterForServiceOrder(queryParams);
  const { data: meterList, isLoading } = useAllMeters(queryParams);
  console.log('Fetched Meter List:', meterList);

  // // Get columns based on operation type
  // const columns = useMemo((): TableColumn<any>[] => {
  //   console.log('Generating columns for operation type:', operationType);

  //   // Installation columns (operationType === 7)
  //   if (operationType === 7) {
  //     return [
  //       {
  //         key: "deviceNumber",
  //         header: "Device Number",
  //         sortable: true,
  //       },
  //       {
  //         key: "manufacturer",
  //         header: "Manufacturer",
  //         sortable: true,
  //       },
  //       {
  //         key: "model",
  //         header: "Model",
  //         sortable: true,
  //       },
  //       {
  //         key: "meterTypeDisplay",
  //         header: "Meter Type",
  //         type: "badge" as const,
  //         badgeConfig: {
  //           variant: "outline" as const,
  //         },
  //       },
  //       {
  //         key: "premise",
  //         header: "Location",
  //         sortable: true,
  //         render: (_: any, row: any) => row.premise || "N/A",
  //       },
  //       {
  //         key: "specifications",
  //         header: "Specifications",
  //         type: "text" as const,
  //       },
  //     ];
  //   }

  //   // Disposal columns (operationType === 17)
  //   if (operationType === 17) {
  //     return [
  //       {
  //         key: "deviceNumber",
  //         header: "Device Number",
  //         sortable: true,
  //       },
  //       {
  //         key: "meterTypeDisplay",
  //         header: "Meter Type",
  //         type: "badge" as const,
  //         badgeConfig: {
  //           variant: "outline" as const,
  //         },
  //       },
  //       {
  //         key: "utilityService",
  //         header: "Utility Service",
  //         sortable: true,
  //         render: (_: any, row: any) => row.consumerInfo?.utilityService || "N/A",
  //       },
  //       {
  //         key: "previousPremise",
  //         header: "Previous Premise",
  //         sortable: true,
  //       },
  //       {
  //         key: "previousConsumerName",
  //         header: "Previous Consumer Name",
  //         type: "custom" as const,
  //         render: (_: any, row: any) => {
  //           const consumer = row.consumerInfo;
  //           if (!consumer) return "N/A";
  //           return `${consumer.firstName || ""} ${consumer.lastName || ""}`.trim();
  //         },
  //       },
  //       {
  //         key: "age",
  //         header: "Age",
  //         sortable: true,
  //       },
  //       {
  //         key: "uninstalledOn",
  //         header: "Uninstalled On",
  //         sortable: true,
  //       },
  //       {
  //         key: "lastReading",
  //         header: "Last Reading",
  //         sortable: true,
  //       },
  //     ];
  //   }

  //   // Replacement columns (operationType === 1)
  //   if (operationType === 1) {
  //     return [
  //       {
  //         key: "meterNumber",
  //         header: "Meter Number",
  //         sortable: true,
  //       },
  //       {
  //         key: "deviceNumber",
  //         header: "Device Number",
  //         sortable: true,
  //       },
  //       {
  //         key: "utilityService",
  //         header: "Utility Service",
  //         sortable: true,
  //         render: (_: any, row: any) => row.consumerInfo?.utilityService || "N/A",
  //       },
  //       {
  //         key: "premise",
  //         header: "Premise",
  //         sortable: true,
  //       },
  //       {
  //         key: "area",
  //         header: "Area",
  //         sortable: true,
  //       },
  //       {
  //         key: "subarea",
  //         header: "Subarea",
  //         sortable: true,
  //       },
  //       {
  //         key: "readingStatus",
  //         header: "Reading Status",
  //         type: "badge" as const,
  //         badgeConfig: {
  //           getVariant: (value: string) => {
  //             switch (value?.toLowerCase()) {
  //               case "read":
  //                 return "default";
  //               case "unread":
  //                 return "secondary";
  //               default:
  //                 return "outline";
  //             }
  //           },
  //         },
  //       },
  //       {
  //         key: "consumerName",
  //         header: "Consumer Name",
  //         type: "custom" as const,
  //         render: (_: any, row: any) => {
  //           const consumer = row.consumerInfo;
  //           if (!consumer) return "N/A";
  //           return `${consumer.firstName || ""} ${consumer.lastName || ""}`.trim();
  //         },
  //       },
  //       {
  //         key: "consumerInfo",
  //         header: "Account Number",
  //         sortable: true,
  //         render: (_: any, row: any) => row.consumerInfo?.consumerNo || "N/A",
  //       },
  //       {
  //         key: "installedDate",
  //         header: "Installed Date",
  //         sortable: true,
  //       },
  //       {
  //         key: "status",
  //         header: "Status",
  //         type: "badge" as const,
  //         badgeConfig: {
  //           getVariant: (value: string) => {
  //             switch (value?.toLowerCase()) {
  //               case "assigned":
  //                 return "default";
  //               case "unassigned":
  //                 return "secondary";
  //               default:
  //                 return "outline";
  //             }
  //           },
  //         },
  //       },
  //       {
  //         key: "lastReading",
  //         header: "Last Reading",
  //         sortable: true,
  //       },
  //     ];
  //   }

  //   // Default columns (when no operation type is selected)
  //   console.log('Using default columns');
  //   return [
  //     {
  //       key: "meterNumber",
  //       header: "Meter Number",
  //       sortable: true,
  //     },
  //     {
  //       key: "meterTypeDisplay",
  //       header: "Type",
  //       type: "badge" as const,
  //       badgeConfig: {
  //         variant: "outline" as const,
  //       },
  //     },
  //   ];
  // }, [operationType]);


  // Get columns based on operation type
const columns = useMemo((): TableColumn<any>[] => {
  console.log('Generating columns for operation type:', operationType);
  
  // Installation columns (operationType === 7)
  if (operationType === 7) {
    return [
      {
        key: "deviceNo", // ✅ Changed from deviceNumber
        header: "Device Number",
        sortable: true,
      },
      {
        key: "manufacturer",
        header: "Manufacturer",
        sortable: true,
        render: (_: any, row: any) => row.manufacturerName || "N/A",
      },
      {
        key: "model",
        header: "Model",
        sortable: true,
        render: (_: any, row: any) => row.modelName || "N/A",
      },
      {
        key: "meterType", // ✅ Changed from meterTypeDisplay
        header: "Meter Type",
        type: "badge" as const,
        badgeConfig: {
          variant: "outline" as const,
        },
      },
      {
        key: "premise", // ✅ This exists in response
        header: "Location",
        sortable: true,
        render: (_: any, row: any) => row.premiseName || "N/A",
      },
      
    ];
  }
  
  // Disposal columns (operationType === 17)
  if (operationType === 17) {
    return [
      {
        key: "deviceNo", // ✅ Changed from deviceNumber
        header: "Device Number",
        sortable: true,
      },
      {
        key: "meterType", // ✅ Changed from meterTypeDisplay
        header: "Meter Type",
        type: "badge" as const,
        badgeConfig: {
          variant: "outline" as const,
        },
      },
      {
        key: "productCode", // ✅ Using productCode from response
        header: "Utility Service",
        sortable: true,
      },
      {
        key: "premise", // ✅ Using premise (can show previous premise if available)
        header: "Previous Premise",
        sortable: true,
        render: (_: any, row: any) => row.premiseName || "N/A",
      },
      {
        key: "firstName", // ✅ Using firstName and lastName
        header: "Previous Consumer Name",
        type: "custom" as const,
        render: (_: any, row: any) => {
          if (!row.firstName && !row.lastName) return "N/A";
          return `${row.firstName || ""} ${row.lastName || ""}`.trim();
        },
      },
      {
        key: "age",
        header: "Age",
        sortable: true,
        render: (_: any, row: any) => {
          // Calculate age from installedOn if available
          if (row.installedOn) {
            const installed = new Date(row.installedOn);
            const now = new Date();
            const ageInYears = Math.floor((now.getTime() - installed.getTime()) / (1000 * 60 * 60 * 24 * 365));
            return `${ageInYears} years`;
          }
          return "N/A";
        },
      },
      {
        key: "updatedOn", // ✅ Using updatedOn for uninstalled date
        header: "Uninstalled On",
        sortable: true,
        render: (_: any, row: any) => row.updatedOn || "N/A",
      },
      {
        key: "meterReading", // ✅ Using meterReading
        header: "Last Reading",
        sortable: true,
        render: (_: any, row: any) => row.meterReading || "N/A",
      },
    ];
  }
  
  // Replacement columns (operationType === 1)
  if (operationType === 1) {
    return [
      {
        key: "meterNumber", // ✅ Correct
        header: "Meter Number",
        sortable: true,
      },
      {
        key: "deviceNo", // ✅ Changed from deviceNumber
        header: "Device Number",
        sortable: true,
      },
      {
        key: "productCode", // ✅ Using productCode
        header: "Utility Service",
        sortable: true,
      },
      {
        key: "premise", // ✅ Correct
        header: "Premise",
        sortable: true,
        render: (_: any, row: any) => {
          // Show premise name if available, otherwise show full premise path
          return row.premiseName || "N/A";
        },
      },
      {
        key: "area",
        header: "Area",
        sortable: true,
        render: (_: any, row: any) => {
          // Show premise name if available, otherwise show full premise path
          return row.areaName || "N/A";
        },
      },
      {
        key: "subarea",
        header: "Subarea",
        sortable: true,
        render: (_: any, row: any) => {
          // Show premise name if available, otherwise show full premise path
          return row.subAreaName || "N/A";
        },
      },
      {
        key: "readingStatus",
        header: "Reading Status",
        type: "badge" as const,
        badgeConfig: {
          getVariant: (value: string) => {
            switch (value?.toLowerCase()) {
              case "read":
                return "default";
              case "unread":
                return "secondary";
              default:
                return "outline";
            }
          },
        },
        render: (_: any, row: any) => {
          // Determine reading status based on meterReading
          return row.meterReading !== null ? "Read" : "Unread";
        },
      },
      {
        key: "firstName", // ✅ Using firstName and lastName
        header: "Consumer Name",
        type: "custom" as const,
        render: (_: any, row: any) => {
          if (!row.firstName && !row.lastName) return "N/A";
          return `${row.firstName || ""} ${row.lastName || ""}`.trim();
        },
      },
      {
        key: "consumerNo", // ✅ Changed from consumerInfo
        header: "Account Number",
        sortable: true,
      },
      {
        key: "installedOn", // ✅ Changed from installedDate
        header: "Installed Date",
        sortable: true,
        render: (_: any, row: any) => row.installedOn || "N/A",
      },
      {
        key: "status", // ✅ Correct
        header: "Status",
        type: "badge" as const,
        badgeConfig: {
          getVariant: (value: string) => {
            switch (value?.toLowerCase()) {
              case "assigned":
                return "default";
              case "unassigned":
                return "secondary";
              default:
                return "outline";
            }
          },
        },
      },
      {
        key: "meterReading", // ✅ Changed from lastReading
        header: "Last Reading",
        sortable: true,
        render: (_: any, row: any) => {
          if (row.meterReading !== null) {
            return `${row.meterReading} ${row.utilityServiceUnit || ""}`.trim();
          }
          return "N/A";
        },
      },
    ];
  }
  
  // Default columns (when no operation type is selected)
  console.log('Using default columns');
  return [
    {
      key: "meterNumber", // ✅ Correct
      header: "Meter Number",
      sortable: true,
    },
    {
      key: "meterType", // ✅ Changed from meterTypeDisplay
      header: "Type",
      type: "badge" as const,
      badgeConfig: {
        variant: "outline" as const,
      },
    },
  ];
}, [operationType]);

// ✅ FIX: Helper function to update URL params WITHOUT losing hash
const updateSearchParams = (updates: Record<string, string | null>) => {
  // Get current hash BEFORE updating params
  const currentHash = window.location.hash;
  console.log("Preserving hash:", currentHash);
  
  const params = new URLSearchParams(searchParams);
  
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  });
  
  // ✅ CRITICAL: Manually construct the new URL with hash preserved
  const newSearch = params.toString();
  const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${currentHash}`;
  
  console.log("New URL with hash:", newUrl);
  
  // Use navigate instead of setSearchParams to have more control
  navigate(newUrl, { replace: true });
};


// Handle apply filters - camelCase in URL
const handleApplyFilters = (newFilters: MeterFiltersType) => {
  const updates: Record<string, string | null> = {
    // Remove all existing filter params (camelCase)
    meterType: null,
    service: null,
    manufacturer: null,
    model: null,
    area: null,
    subarea: null,
    premise: null,
    readingStatus: null,
    dateFrom: null,
    dateTo: null,
    // Reset to page 1
    page: "1",
  };

  // Add new filter params (camelCase)
  Object.entries(newFilters).forEach(([key, value]) => {
    if (value) {
      updates[key] = value;
    }
  });

  updateSearchParams(updates);
};

// Handle clear filters - camelCase
const handleClearFilters = () => {
  const updates: Record<string, string | null> = {
    meterType: null,
    service: null,
    manufacturer: null,
    model: null,
    area: null,
    subarea: null,
    premise: null,
    readingStatus: null,
    dateFrom: null,
    dateTo: null,
    search: null,
    page: "1",
  };

  updateSearchParams(updates);
};

  // Handle search
  const handleSearch = (searchTerm: string) => {
    updateSearchParams({
      search: searchTerm.trim() || null,
      page: "1",
    });
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    updateSearchParams({
      page: String(page),
    });
  };

  // Handle selection change
  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedMeters(selectedIds);
  };

  // Pagination config
  const pagination = meterList
    ? {
        currentPage,
        totalPages: Math.ceil(meterList.count / PAGE_SIZE),
        totalItems: meterList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, meterList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  // Check if filters are active
  const hasActiveFilters = Object.values(currentFilters).some(
    (value) => value !== undefined && value !== ""
  );

  // Handle next step
  const handleNext = () => {
    clearValidationError?.();

    if (selectedMeters.length === 0) {
      setValidationError?.("Please select at least one meter to continue");
      return;
    }

    // Get selected meter details
    const selectedMeterDetails = (meterList?.results || []).filter((meter) =>
      selectedMeters.includes(String(meter.id))
    );

    // Save data using step helpers
    if (stepHelpers) {
      stepHelpers.setStepData(currentStepIndex, {
        selectedMeterIds: selectedMeters,
        selectedMeterCount: selectedMeters.length,
        selectedMeters: selectedMeterDetails,
      });
    }

    // Update parent component
    onDataChange?.({
      selectedMeterIds: selectedMeters,
      selectedMeterCount: selectedMeters.length,
      selectedMeters: selectedMeterDetails,
    });

    // Move to next step
    onNext?.();
  };

  // Show loading or message when no operation type is selected
  if (!operationType) {
    return (
      <div className="space-y-6">
        <div className="p-8 text-center border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">
            Please select an operation type in the previous step to continue
          </p>
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <Button variant="outline" onClick={onPrevious}>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Previous
          </Button>
          <Button onClick={handleNext} disabled={true}>
            Next
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Operation Type Badge */}
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-sm py-1 px-3">
          Operation: {operationType === 7 ? 'Installation' : operationType === 17 ? 'Disposal' : operationType === 1 ? 'Replacement' : operationType}
        </Badge>
        {selectedMeters.length > 0 && (
          <Badge variant="default" className="text-sm py-1 px-3">
            {selectedMeters.length} meter{selectedMeters.length !== 1 ? "s" : ""}{" "}
            selected
          </Badge>
        )}
      </div>

      {/* Meter Table */}
      <GenericTable
        data={meterList?.results ?? []}
        columns={columns}
        rowKey="id"
        selectable={true}
        selectedRows={selectedMeters}
        onSelectionChange={handleSelectionChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        searchPlaceholder="Search meters by device number or location"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No meters found"
        filterPanel={
          <MeterFilters
            filters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            remoteUtilityId={remoteUtilityId}
            operationType={operationType}
            isLoading={isLoading}
          />
        }
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={onPrevious}>
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Previous
        </Button>
        <Button onClick={handleNext} disabled={selectedMeters.length === 0}>
          Next
          <svg
            className="w-4 h-4 ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Button>
      </div>
    </div>
  );
};